import ROUTE from '@us/app/router/appModule/names'
import { decodeQuery } from '@shared/utils.js'
import SIGNUP_METHODS from '@us/app/config/signup-methods.js'
import VERIFICATION_METHODS from '@us/app/config/verification-methods.js'
import useAuthStore from '@shared/stores/useAuthStore.js'
import usePersistedConfigStore from '@shared/stores/usePersistedConfigStore.js'
import useUserStore from '@shared/stores/useUserStore.js'
import useUsStore from '@us/app/stores/useUsStore.js'
import * as apiRequest from '@shared/api-endpoints'
import useSharedStore from '@shared/stores/useSharedStore.js'
import useLandingPageStore from '@shared/stores/useLandingPageStore.js'
import { isTerminatedForSwordMigration } from '@shared/data/models/corporate.js'

const landingPageVerificationTypeToRoute = {
  eligibility_list: ROUTE.VERIFY_WITH_ELIGIBILITY_LIST,
  voucher: ROUTE.VERIFY_WITH_VOUCHER,
  solera: ROUTE.VERIFY_WITH_SOLERA,
  uhchub: ROUTE.VERIFY_WITH_UHCHUB,
  uhcstore: ROUTE.VERIFY_WITH_UHCSTORE,
  'webmd-one': ROUTE.VERIFY_WITH_WEBMD_ONE,
  virginpulse_sso: ROUTE.SAML_SSO_INITIATED,
  sword: ROUTE.SWORD_REDIRECT,
}

export default async (routeTo) => {
  const authStore = useAuthStore()
  const persistedConfigStore = usePersistedConfigStore()
  const userStore = useUserStore()
  const usStore = useUsStore()
  const sharedStore = useSharedStore()
  const landingPageStore = useLandingPageStore()

  const redirectTo = (name, qs, params) => ({
    name,
    query: qs || routeTo.query,
    params: params || routeTo.params,
  })

  let slug = (
    routeTo.params.slug ||
    routeTo.query.corporate ||
    ''
  ).toLowerCase()

  sharedStore.storeInitialQuery(decodeQuery(routeTo.query))
  const initialQuery = sharedStore.initialQuery

  // handle routes that don't need a corporate object
  switch (routeTo.name) {
    case ROUTE.DEEPLINK_FALLBACK:
    case ROUTE.PASSWORD_RESET:
    case ROUTE.PARTNER_SEARCH:
    case ROUTE.INIT_UHCHUB:
    case ROUTE.INIT_WEBMD_ONE:
    case ROUTE.INIT_PERSONIFY_HEALTH:
    case ROUTE.SWORD_REDIRECT:
      return true
  }

  const landingPage = await landingPageStore.ensureLandingPage(slug)

  if (!landingPage) {
    return redirectTo(
      ROUTE.PARTNER_SEARCH,
      { unresolved_corporate_key: slug },
      {},
    )
  }

  const routeRequiresCorporate = new Set([
    ROUTE.SIGN_UP,
    ROUTE.LOGIN,
    ROUTE.SUBSCRIPTION,
    ROUTE.DOWNLOAD_LINK,
    ROUTE.SAML_SSO_COMPLETED,
  ])

  if (routeTo.name !== ROUTE.LANDING_PAGE) {
    let corporate
    try {
      corporate = await apiRequest.loadCorporate({ key: slug })
    } catch {
      corporate = null
    }

    if (corporate && !corporate.isCustomer) {
      if (isTerminatedForSwordMigration(corporate)) {
        return redirectTo(ROUTE.SWORD_REDIRECT, {}, {})
      }
      // TODO pass corporate key to partner search and show not customer warning there
      return redirectTo(
        ROUTE.PARTNER_SEARCH,
        { unresolved_corporate_key: corporate.key },
        {},
      )
    }

    if (!corporate && routeRequiresCorporate.has(routeTo.name)) {
      return redirectTo(
        ROUTE.PARTNER_SEARCH,
        { unresolved_corporate_key: slug },
        {},
      )
    }
  }

  // handle routes that need a corporate object
  switch (routeTo.name) {
    case ROUTE.LANDING_PAGE: {
      if (
        landingPage?.skipLandingPage ||
        landingPage?.eligibilityVerificationType === 'sword'
      ) {
        return redirectTo(ROUTE.SELECT_VERIFICATION_METHOD)
      }

      // skip the information screen if its a login flow
      if (
        initialQuery.login === 'true' ||
        initialQuery.skip_information ||
        authStore.isAuthenticated
      ) {
        return redirectTo(
          ROUTE.SELECT_VERIFICATION_METHOD,
          {},
          {
            slug,
          },
        )
      }

      // decide whether to show the incentive banner
      persistedConfigStore.assignIncentiveType(landingPage)
      break
    }

    case ROUTE.SELECT_VERIFICATION_METHOD: {
      let voucherCode = initialQuery.voucher_code || initialQuery.voucher
      const email = initialQuery.email

      const landingPageVerificationType =
        landingPage?.eligibilityVerificationType

      if (landingPageVerificationType) {
        if (
          landingPageVerificationType === 'redirect' &&
          landingPage?.eligibilityVerificationExternalUrl
        ) {
          window.location.assign(landingPage.eligibilityVerificationExternalUrl)
          return false
        }

        const routeName =
          landingPageVerificationTypeToRoute[landingPageVerificationType]

        if (routeName) {
          return {
            ...routeTo,
            name: routeName,
            params: {
              slug: landingPage.slug,
              ...(landingPageVerificationType === SIGNUP_METHODS.VOUCHER &&
                voucherCode && { voucherCode }),
            },
            query: {
              ...routeTo.query,
              ...(email && { email }),
            },
          }
        }
      }

      return redirectTo(
        ROUTE.PARTNER_SEARCH,
        { unresolved_corporate_key: slug },
        {},
      )
    }

    case ROUTE.SAML_SSO_INITIATED:
      if (userStore.user) {
        return redirectTo(ROUTE.SAML_SSO_COMPLETED)
      }
      break

    case ROUTE.SAML_SSO_COMPLETED:
      if (!authStore.isAuthenticated) {
        return redirectTo(ROUTE.SAML_SSO_ERROR, { code: 'UNAUTHORIZED' })
      }

      // Block the user from going any further without a valid subscription.
      // This should be removed as soon as subscriptions are handled correctly in the apps.
      if (!userStore.user?.hasActiveSubscription) {
        return redirectTo(ROUTE.SAML_SSO_ERROR, {
          code: 'NO_ACTIVE_SUBSCRIPTION',
        })
      }

      if (userStore.user?.profile?.hasAcceptedDataProtection) {
        return redirectTo(ROUTE.DOWNLOAD_LINK)
      }
      break

    case ROUTE.SIGN_UP: {
      if (authStore.isAuthenticated) {
        return redirectTo(ROUTE.SUBSCRIPTION)
      }

      if (initialQuery.login === 'true') {
        return redirectTo(ROUTE.LOGIN)
      }

      if (
        !Object.values(VERIFICATION_METHODS).includes(
          usStore.corporateEligibilityData.type,
        )
      ) {
        return redirectTo(ROUTE.SELECT_VERIFICATION_METHOD)
      }
      break
    }

    case ROUTE.LOGIN:
      if (authStore.isAuthenticated) {
        return redirectTo(ROUTE.SUBSCRIPTION)
      }
      break

    case ROUTE.SUBSCRIPTION:
      if (!authStore.isAuthenticated) {
        return redirectTo(ROUTE.SIGN_UP)
      }

      if (
        !Object.values(VERIFICATION_METHODS).includes(
          usStore.corporateEligibilityData.type,
        )
      ) {
        return redirectTo(ROUTE.SELECT_VERIFICATION_METHOD)
      }
      break

    case ROUTE.DOWNLOAD_LINK:
      if (!authStore.isAuthenticated) {
        return redirectTo(ROUTE.LOGIN)
      }
      break

    default:
      break
  }
}
